// ─────────────────────────────────────────────
//  useLaneCreate — clicking empty lane space to place a new case.
//
//  ⚠️ A CREATE PAGES PHONES, UNCONDITIONALLY (`AssignmentModal.js:318` —
//  `sendNotice` is true for a new case). That is right: a new assignment is news.
//  It is also why this gesture has to be DELIBERATE. Everything here exists to
//  make an accidental one impossible:
//    · only a click whose target IS the lane — a click on a bar bubbles up here
//      and must not count;
//    · only a click whose OWN pointerdown landed on the lane and barely moved.
//      A drag handle stops its pointerdown from reaching the lane, so the click
//      a browser synthesises at the end of a drag finds no matching press here
//      and is dropped;
//    · never when the lane cannot be measured, because then the time would be
//      invented from a zero-width rect;
//    · never when the page is not on today's date — `AssignmentModal` stamps its
//      row with `rosterDateKey()` regardless of the date it was opened over, so
//      a click while browsing Thursday would write a real assignment onto TODAY's
//      live board.
//
//  It returns the seed for `AssignmentModal`, not a dialog of its own: the repo
//  already carries a scar about inventing a second placement surface
//  (TimelineChartPage.js:747-750 — "find the component the working screen already
//  renders and mount THAT"), and there are two time-editing surfaces already.
//
//  This is an ACCELERATOR, not the only way in. The page's ＋ Add assignment
//  button opens the same modal for anyone who cannot use a pointer.
// ─────────────────────────────────────────────
import { useCallback, useRef, useState } from "react";
import { laneCreateTimes } from "../../domain/timelineGeometry";

// How far the pointer may travel between press and release and still be a click.
// A ratio of the measured lane, not a pixel count — wall mode scales this page
// and a stored pixel would mean a different distance at every zoom.
const CLICK_SLOP_RATIO = 0.004;
const CLICK_SLOP_FLOOR = 2;

export default function useLaneCreate({ enabled, axisStart, span, toast, blockedReason }) {
  // { roomId, roomName, startTime, endTime } while the create dialog is open.
  const [laneCreate, setLaneCreate] = useState(null);
  const downRef = useRef(null);

  const onTrackPointerDown = useCallback((e) => {
    // A press that started on a bar (or anywhere but bare lane) disqualifies the
    // click that follows it.
    if (e.target !== e.currentTarget) {
      downRef.current = null;
      return;
    }
    downRef.current = { x: e.clientX, y: e.clientY, node: e.currentTarget };
  }, []);

  const onTrackClick = useCallback(
    (e, room) => {
      const down = downRef.current;
      downRef.current = null; // one press, one click
      if (!enabled) return;
      if (e.target !== e.currentTarget) return;
      if (!down || down.node !== e.currentTarget) return;

      // ⚠️ FAIL CLOSED ON A PRESS WE CANNOT COMPARE. A comparison against NaN is
      // false, so an unreadable coordinate would sail through the travel check
      // below and create a case from a drag.
      if (!Number.isFinite(down.x) || !Number.isFinite(down.y)) return;
      if (!Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const slop = Math.max(CLICK_SLOP_FLOOR, (rect.width || 0) * CLICK_SLOP_RATIO);
      if (Math.abs(e.clientX - down.x) > slop || Math.abs(e.clientY - down.y) > slop) return;

      // Named rather than silently ignored: an operator who clicks and gets
      // nothing assumes the feature is broken.
      if (blockedReason) {
        if (toast) toast.info(blockedReason);
        return;
      }

      const times = laneCreateTimes({ clientX: e.clientX, rect, axisStart, span });
      if (!times) return;
      setLaneCreate({
        roomId: room.id,
        roomName: room.name,
        startTime: times.startTime,
        endTime: times.endTime,
      });
    },
    [enabled, axisStart, span, toast, blockedReason]
  );

  const closeLaneCreate = useCallback(() => setLaneCreate(null), []);

  return { laneCreate, onTrackPointerDown, onTrackClick, closeLaneCreate };
}
